import { toastr } from 'react-redux-toastr';

import authHeader from './authHeader';
import auth from './auth/index';
import analysis from './analysis';
import payments from './payments';
import error from './error';
import api from './api/index';
import webChecker from './webChecker';
import adminAccounts from './adminAccounts';
import getMessageFromError from 'functions/general/getMessageFromError';
import getLocal from 'services/getLocal';
import type { HTTPFunctions, ApiHTTP, GmailSignInData, ResponseWithData } from './types';
import type { IntUserResponse } from 'services/local/types';

const catcher = <T>(callback: Promise<T> | undefined) => {
  try {
    return callback;
  } catch (e: unknown) {
    toastr.error(getMessageFromError(e), '');
  }
}

const hasNotValidCredentials = (message: string) =>
  ['Request failed with status code 401'].includes(message);

const checkCredentials = <T>(prom: Promise<T>) =>
  prom.then(response => {
    return Promise.resolve(response);
  }).catch((errorResponse: unknown) => {
    const message = getMessageFromError(errorResponse);
    if (hasNotValidCredentials(message)) {
      if (getLocal(localStorage).auth.getUser()) {
        getLocal(localStorage).auth.delUser();
        if (window.location.origin.startsWith('https://analytics.tigim.co')) {
          window.location.replace('https://analytics.tigim.co/integrations');
        } else {
          window.location.replace('/');
        }
      }
    }
    if (errorResponse instanceof Error) return Promise.reject(errorResponse);
    return Promise.reject(new Error(message));
  });

type HttpCreator = {
  create: (data: any) => HttpInstance;
}

type PostishFunction = <T>(url: string, data: unknown, config?: any) => Promise<ResponseWithData<T>>;

export type HttpInstance = {
  get: <T>(url: string, config?: any) => Promise<ResponseWithData<T>>,
  post: PostishFunction,
  put: PostishFunction,
  delete: <T>(url: string, data: any, config?: any) => Promise<ResponseWithData<T>>,
  patch: PostishFunction
}

const getFunctions = (http: HttpCreator, apiURL: string | undefined, wrapper: <T>(prom: Promise<ResponseWithData<T>> | undefined) => Promise<T>): HTTPFunctions => {
  const instance = http.create({ baseURL: `${apiURL}` });
  return {
    instance,
    wrapper,
    get: async (url: string) => wrapper(catcher(checkCredentials(instance.get(url, { headers: authHeader()})))),
    getPDF: async (url: string) => wrapper(catcher(checkCredentials(instance.get(url, {
      headers: { ...authHeader(),
        'Content-type': 'application/pdf',
      },
      responseType: 'blob'
    })))),
    post: async <T>(url: string, data: unknown) => wrapper(catcher(checkCredentials(instance.post(url, data, { headers: authHeader()})
      .catch((e: unknown) => {
        if (e instanceof Error) return Promise.reject(e);
        return Promise.reject(new Error(getMessageFromError(e)));
      })))) as T,
    postGmail: async (url: string, data: GmailSignInData) => wrapper(catcher(checkCredentials(instance.post<IntUserResponse>(
      url,
      data,
      { headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-Google-OAuth2-Type': 'client'
    }})).catch((e: unknown) => {
      if (e instanceof Error) return Promise.reject(e);
      return Promise.reject(new Error(getMessageFromError(e)));
    }))),
    postFile: async (url: string, data: FormData) => wrapper(catcher(checkCredentials(instance.post(
      url,
      data,
      { headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data'
      }}
    )))),
    put: async <T>(url: string, data: T) => wrapper(catcher(checkCredentials(instance.put(url, data, { headers: authHeader()})))),
    patch: async <T>(url: string, data: T) => wrapper(catcher(checkCredentials(instance.patch(url, data, { headers: authHeader()})))),
    del: async (url: string) => wrapper(catcher(checkCredentials(instance.delete(url, { headers: authHeader()})))),
    delData: async <T>(url: string, data: T) => wrapper(catcher(checkCredentials(instance.delete(url, {data, headers: authHeader()})))),
  }
}

const apiHttp = (http: HttpCreator, wrapper: <T>(prom: Promise<ResponseWithData<T>> | undefined) => Promise<T>): ApiHTTP => ({
  auth: auth(getFunctions(http, process.env.REACT_APP_AUTH_HOST, wrapper)),
  analysis: analysis(getFunctions(http, process.env.REACT_APP_ANALYSIS_HOST, wrapper)),
  payments: payments(getFunctions(http, process.env.REACT_APP_PAYMENTS_HOST, wrapper)),
  error: error(getFunctions(http, process.env.REACT_APP_NOTIFICATIONS_HOST, wrapper)),
  api: api(getFunctions(http, process.env.REACT_APP_API_HOST, wrapper)),
  webChecker: webChecker(getFunctions(http, process.env.REACT_APP_WEB_CHECKER_HOST, wrapper)),
  admin: {
    accounts: adminAccounts(getFunctions(http, process.env.REACT_APP_ADMIN_HOST, wrapper))
  }
});

export default apiHttp;
